import { useEffect, useCallback } from 'react';
import { AnyAction } from 'redux';
import { useDispatch } from 'react-redux';
import { option, array } from 'fp-ts';
import { pipe, flow } from 'fp-ts/function';
import { isDefined, isUndefined } from 'utils/tsHelpers';
import { Catalog, CatalogFieldDetails, FieldType } from 'modules/domains';
import { useFetchCatalogFields } from 'modules/catalogFieldTypes/hooks';
import { FieldOption } from 'modules/queryBuilder/legacyQueryBuilder/types';
import {
  initializeQueryResponse,
  toggleViewMode,
} from 'modules/queryBuilder/legacyQueryBuilder/actions';
import { useCatalogs } from 'modules/catalog/hooks';
import { useCatalogContext } from 'modules/catalog/catalogSelector';
import { useCatalogSelectionContext } from '../contexts/CatalogSelectionContext';
import { useCatalogQueryContext } from '../../../contexts/catalogQuery';
import { useCatInSegQueryBuilderV2FeatureFlag } from './useCatInSegQueryBuilderV2FeatureFlag';

const getCatalogFieldType = (
  catalogFieldTypes: FieldOption[],
  catalogField?: string,
): FieldOption | undefined =>
  pipe(
    catalogFieldTypes,
    array.findFirst(({ name }) => name === catalogField),
    option.toUndefined,
  );

const getCatalog = (catalogName?: string): ((catalogs: Catalog[]) => option.Option<Catalog>) =>
  flow(array.findFirst(({ name }) => name === catalogName));

const getCatalogFieldDetails: (
  catalogFieldType?: FieldOption,
) => option.Option<CatalogFieldDetails> = flow(
  option.fromNullable,
  option.map(({ name, type }) => ({ field: name, fieldType: type })),
  option.alt(() => option.some({ fieldType: FieldType.STRING as FieldType })),
);

export const useUpdateCatalogDrawer = () => {
  const dispatch = useDispatch();

  const isFeatureFlagged = useCatInSegQueryBuilderV2FeatureFlag();
  const { update, setIsEnabled } = useCatalogSelectionContext();
  const { catalogQueryDetails, resetCatalogQuery } = useCatalogQueryContext();
  const { change, changeFieldDetails } = useCatalogContext();
  const { data: catalogs = [] } = useCatalogs();
  const { data: catalogFieldTypes = [] } = useFetchCatalogFields(
    catalogQueryDetails?.catalogQuery?.catalogName,
  );

  const catalogQuery = catalogQueryDetails?.catalogQuery;
  const catalogFieldType = getCatalogFieldType(
    catalogFieldTypes,
    catalogQueryDetails?.catalogField,
  );

  const toggleBuilderView = useCallback(() => dispatch(toggleViewMode() as AnyAction), []);
  const initializeCatalogQuery = useCallback(
    (searchQueries: any, combinator: any) =>
      dispatch(
        initializeQueryResponse({
          searchQueries,
          combinator,
          hasDynamicFields: false,
        }) as AnyAction,
      ),
    [initializeQueryResponse],
  );

  // update catalog field
  useEffect(() => {
    const catalog = getCatalog(catalogQuery?.catalogName)(catalogs);
    if (catalogQuery) change(catalog);
  }, [catalogQuery]);

  // update catalog field selector
  useEffect(() => {
    const catalogFieldDetails = getCatalogFieldDetails(catalogFieldType);
    if (catalogQuery) changeFieldDetails(catalogFieldDetails);
  }, [catalogQuery, catalogFieldTypes]);

  // update builder
  const searchQuery = catalogQuery?.searchQuery;
  useEffect(() => {
    const hasCatalogFieldType = !isDefined(catalogFieldType);
    if (isFeatureFlagged) {
      if (isUndefined(catalogQuery)) resetCatalogQuery();
      setIsEnabled(isDefined(searchQuery));
    }
    if (isDefined(searchQuery?.combinator) && isDefined(searchQuery?.searchQueries)) {
      /**
       * A bit hacky but we want to call "initializeQueryResponse" after legacy queryBuilders first render.
       * SetTimeout allows us to make these fn calls down the execution context
       **/
      setTimeout(() => {
        initializeCatalogQuery(searchQuery.searchQueries, searchQuery.combinator); // update builder
        toggleBuilderView(); // update builder view
        update(hasCatalogFieldType); // update catalog match (key or property)
      });
    }
  }, [searchQuery, catalogFieldType]);
};
